import React, { useEffect, useState } from 'react';
import './ApplyScheduleTargetsToPatientsModal.css';

const APPLY_OPTIONS = {
  MATCHING: 'matching',
  ALL: 'all',
  PRACTICE_ONLY: 'practice_only'
};

const ApplyScheduleTargetsToPatientsModal = ({
  isOpen,
  matchingCount = 0,
  sectionLabel = 'these settings',
  applying = false,
  onClose,
  onConfirm
}) => {
  const [selectedOption, setSelectedOption] = useState(APPLY_OPTIONS.MATCHING);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedOption(
      matchingCount > 0 ? APPLY_OPTIONS.MATCHING : APPLY_OPTIONS.PRACTICE_ONLY
    );
  }, [isOpen, matchingCount]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !applying) onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, applying, onClose]);

  if (!isOpen) return null;

  const matchingLabel =
    matchingCount === 1
      ? '1 patient currently matches'
      : `${matchingCount} patients currently match`;

  return (
    <div
      className="sts-apply-modal-overlay"
      role="presentation"
      onClick={() => !applying && onClose?.()}
    >
      <div
        className="sts-apply-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sts-apply-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sts-apply-modal-header">
          <h3 id="sts-apply-modal-title">Update patient schedule &amp; target settings?</h3>
          <button
            type="button"
            className="sts-apply-modal-close"
            onClick={onClose}
            disabled={applying}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="sts-apply-modal-body">
          <div className="sts-apply-modal-banner">
            <p>
              You are about to save new practice <strong>{sectionLabel}</strong>.
              Choose how these changes should apply to patients in this practice.
            </p>
          </div>

          <div className="sts-apply-option-list" role="radiogroup" aria-label="Patient update options">
            <label
              className={`sts-apply-option-card${selectedOption === APPLY_OPTIONS.MATCHING ? ' is-selected' : ''}${matchingCount === 0 ? ' is-disabled' : ''}`}
            >
              <input
                type="radio"
                name="sts-apply-option"
                value={APPLY_OPTIONS.MATCHING}
                checked={selectedOption === APPLY_OPTIONS.MATCHING}
                disabled={applying || matchingCount === 0}
                onChange={() => setSelectedOption(APPLY_OPTIONS.MATCHING)}
              />
              <div className="sts-apply-option-content">
                <div className="sts-apply-option-title">
                  Only update matching patients
                  <span className="sts-apply-option-badge">{matchingLabel}</span>
                </div>
                <p className="sts-apply-option-desc">
                  Update only patients whose current schedule and target settings are the same as
                  this practice. Patients with customized settings will not be changed.
                </p>
              </div>
            </label>

            <label
              className={`sts-apply-option-card${selectedOption === APPLY_OPTIONS.ALL ? ' is-selected' : ''}`}
            >
              <input
                type="radio"
                name="sts-apply-option"
                value={APPLY_OPTIONS.ALL}
                checked={selectedOption === APPLY_OPTIONS.ALL}
                disabled={applying}
                onChange={() => setSelectedOption(APPLY_OPTIONS.ALL)}
              />
              <div className="sts-apply-option-content">
                <div className="sts-apply-option-title">Update all patients</div>
                <p className="sts-apply-option-desc">
                  Apply these new practice schedule and target settings to every patient in this
                  practice, including patients who currently have different/customized settings.
                </p>
              </div>
            </label>

            <label
              className={`sts-apply-option-card${selectedOption === APPLY_OPTIONS.PRACTICE_ONLY ? ' is-selected' : ''}`}
            >
              <input
                type="radio"
                name="sts-apply-option"
                value={APPLY_OPTIONS.PRACTICE_ONLY}
                checked={selectedOption === APPLY_OPTIONS.PRACTICE_ONLY}
                disabled={applying}
                onChange={() => setSelectedOption(APPLY_OPTIONS.PRACTICE_ONLY)}
              />
              <div className="sts-apply-option-content">
                <div className="sts-apply-option-title">Save practice settings only</div>
                <p className="sts-apply-option-desc">
                  Save the practice settings now, but do not change any patient schedule or target
                  settings.
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="sts-apply-modal-footer">
          <button
            type="button"
            className="sts-apply-btn sts-apply-btn-cancel"
            onClick={onClose}
            disabled={applying}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sts-apply-btn sts-apply-btn-save"
            disabled={applying}
            onClick={() => onConfirm?.(selectedOption)}
          >
            {applying ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplyScheduleTargetsToPatientsModal;
export { APPLY_OPTIONS };
